import { createSignal, Signal } from '../core/Signal'

export interface DataHubStorageConfig {
  lazyLoad?: boolean
  invalidationStrategy?: 'time-based' | 'manual' | 'hybrid'
  checkTime?: number
  canDirty?: boolean // Indica si los datos pueden marcarse como sucios (dirty) para forzar una recarga
  canStaled?: boolean // Indica si los datos pueden marcarse como obsoletos (staled) para forzar una recarga en la próxima consulta
}

export const defaultDataHubStorageConfig: DataHubStorageConfig = {
  lazyLoad: false,
  invalidationStrategy: 'manual',
  checkTime: 0, // Se usa para verificar periódicamente si los datos necesitan ser recargados, solo relevante para estrategias que no sean manual o dependiente de datos dirty/staled
  canDirty: true,
  canStaled: true,
}

export interface DataHubStorageInfo {
  lastUpdated: Date | null
  subscribersCount: number
  isDirty: boolean
  isStaled: boolean
}

export const defaultDataHubStorageInfo: DataHubStorageInfo = {
  lastUpdated: null,
  subscribersCount: 0,
  isDirty: false,
  isStaled: false,
}

export enum DataHubLifecycle {
  INITIAL,
  LOADING,
  LOADED,
  ERROR,
}

export enum DataHubOperationType {
  MAP = 'map',
  FILTER = 'filter',
  SORT = 'sort',
  CUSTOM = 'custom',
}

export interface DataHubOperation<I, O> {
  type: DataHubOperationType
  name: string
  execute: (data: I[]) => O[]
}

export interface DataHubSubscriber<O> {
  id: string
  callback: (data: O[]) => void
  lastReceivedData: O[] | null
}

export interface DataHubPipelineConfig {
  name: string
  operations: DataHubOperation<any, any>[]
}

// Conjunto de operaciones, la primera debe ingresarse datos con tipo I, y la última debe retornar datos con tipo O.
// Lo unico importante es que la primera operación reciba datos con tipo I, y la última retorne datos con tipo O, las operaciones intermedias pueden recibir y retornar cualquier tipo.
export interface DataHubPipeline<I, O> {
  name: string
  operations: DataHubOperation<any, any>[] // Primera operación debe recibir I, última operación debe retornar O
  dataSignal: Signal<O[]>
  subscribers: Set<DataHubSubscriber<O>>
  lastUpdate: Date | null
}

export interface DataHubStorage<I, O> {
  dataGetter: () => Promise<I[]>
  pipelines: Array<DataHubPipeline<any, any>> // La primera operación de la primera pipeline debe recibir I, y la última operación de la última pipeline debe retornar O
  dataSignal: Signal<O[]>
  subscribers: Set<DataHubSubscriber<O>>
  state: DataHubLifecycle
  config: DataHubStorageConfig
  info: DataHubStorageInfo
  intervalId: number | null
}

export interface DataHubStartupConfig extends DataHubStorageConfig {
  pipelines?: Array<DataHubPipelineConfig>
}

export interface DataHubStorageOperations<O = any> {
  getData: () => O[]
  markDirty: () => void
  markStaled: () => void
  forceRefresh: () => Promise<void>
}

export interface DataHubSubscriptionOperations<O = any> extends DataHubStorageOperations<O> {
  removeSubscriber: () => void
}

class DataHubManager {
  private storages: Map<string, DataHubStorage<any, any>>

  constructor() {
    this.storages = new Map<string, DataHubStorage<any, any>>()
  }

  public startup() {
    // Aquí podríamos cargar configuraciones iniciales, si es necesario
  }

  public registerStorage<I, O>(
    storageID: string,
    dataGetter: () => Promise<I[]>,
    config: DataHubStartupConfig = {},
  ): DataHubStorageOperations<O> {
    if (this.storages.has(storageID)) {
      throw new Error(`Storage with ID ${storageID} already exists`)
    }
    const mergedConfig = { ...defaultDataHubStorageConfig, ...config }
    const pipelines: DataHubPipeline<any, any>[] = []
    if (mergedConfig.pipelines) {
      mergedConfig.pipelines.forEach((pipelineConfig) => {
        const pipeline: DataHubPipeline<any, any> = {
          name: pipelineConfig.name,
          operations: pipelineConfig.operations,
          dataSignal: createSignal<any[]>([]),
          subscribers: new Set<DataHubSubscriber<any>>(),
          lastUpdate: null,
        }
        pipelines.push(pipeline)
      })
    }
    const storage: DataHubStorage<I, O> = {
      dataGetter,
      pipelines: pipelines,
      dataSignal: createSignal<O[]>([]),
      subscribers: new Set<DataHubSubscriber<O>>(),
      state: DataHubLifecycle.INITIAL,
      config: mergedConfig,
      info: { ...defaultDataHubStorageInfo },
      intervalId: null,
    }
    this.storages.set(storageID, storage)
    if (!mergedConfig.lazyLoad) {
      this.refreshStorage(storageID)
    }
    // Si se ha definido una estrategia de invalidación basada en tiempo, configurar un intervalo para la verificación automática
    if (
      mergedConfig.checkTime &&
      mergedConfig.checkTime > 0 &&
      (mergedConfig.invalidationStrategy !== 'manual' || mergedConfig.canDirty || mergedConfig.canStaled)
    ) {
      const intervalId = window.setInterval(() => {
        this.autoRefreshCheck(storageID)
      }, mergedConfig.checkTime)
      storage.intervalId = intervalId
    }
    return {
      getData: () => storage.dataSignal.get(),
      markDirty: () => this.markStorageDirty(storageID),
      markStaled: () => this.markStorageStaled(storageID),
      // Si la estrategia de invalidación no es manual,
      //  la función de forzar refresco lanzara un throw indicando que no es posible forzar el refresco,
      //  ya que la recarga se maneja automáticamente según la configuración.
      // Si la estrategia es manual, entonces se permitirá forzar el refresco.
      forceRefresh: () =>
        mergedConfig.invalidationStrategy === 'time-based'
          ? Promise.reject(new Error('Force refresh is not allowed for non-manual invalidation strategies'))
          : this.refreshStorage(storageID),
    }
  }

  public subscribeStorage<O>(
    storageID: string,
    subscriberID: string,
    callback: (data: O[]) => void,
  ): DataHubSubscriptionOperations<O> {
    console.log(`Subscribing to storage ${storageID} with subscriber ID ${subscriberID}`)
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    const subscriber: DataHubSubscriber<O> = {
      id: subscriberID,
      callback,
      lastReceivedData: null,
    }

    storage.subscribers.add(subscriber)
    if (storage.info.lastUpdated) {
      this.notifySubscriber(storageID, subscriberID)
    }
    return {
      getData: () => storage.dataSignal.get(),
      removeSubscriber: () => this.removeSubscriber(storageID, subscriberID),
      markDirty: () => this.markStorageDirty(storageID),
      markStaled: () => this.markStorageStaled(storageID),
      forceRefresh: () =>
        storage.config.invalidationStrategy === 'time-based'
          ? Promise.reject(new Error('Force refresh is not allowed for non-manual invalidation strategies'))
          : this.refreshStorage(storageID),
    }
  }

  public subscribePipeline<O>(
    storageID: string,
    pipelineName: string,
    subscriberID: string,
    callback: (data: O[]) => void,
  ): DataHubSubscriptionOperations<O> {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    const pipeline = storage.pipelines.find((p) => p.name === pipelineName)
    if (!pipeline) {
      throw new Error(`Pipeline with name ${pipelineName} not found in storage ${storageID}`)
    }
    const subscriber: DataHubSubscriber<O> = {
      id: subscriberID,
      callback,
      lastReceivedData: null,
    }
    pipeline.subscribers.add(subscriber)
    if (pipeline.lastUpdate) {
      this.notifyPipelineSubscriber(storageID, pipelineName, subscriberID)
    }
    return {
      getData: () => pipeline.dataSignal.get(),
      removeSubscriber: () => this.removePipelineSubscriber(storageID, pipelineName, subscriberID),
      markDirty: () => this.markStorageDirty(storageID),
      markStaled: () => this.markStorageStaled(storageID),
      forceRefresh: () => this.refreshPipeline(storageID, pipelineName),
    }
  }

  private removeSubscriber(storageID: string, subscriberID: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    storage.subscribers.forEach((sub) => {
      if (sub.id === subscriberID) {
        storage.subscribers.delete(sub)
      }
    })
  }

  private removePipelineSubscriber(storageID: string, pipelineName: string, subscriberID: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    const pipeline = storage.pipelines.find((p) => p.name === pipelineName)
    if (!pipeline) {
      throw new Error(`Pipeline with name ${pipelineName} not found in storage ${storageID}`)
    }
    pipeline.subscribers.forEach((sub) => {
      if (sub.id === subscriberID) {
        pipeline.subscribers.delete(sub)
      }
    })
  }

  private async autoRefreshCheck(storageID: string): Promise<void> {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    const now = new Date()
    const timeDiff = storage.info.lastUpdated ? now.getTime() - storage.info.lastUpdated.getTime() : null
    const timeCheck =
      storage.config.invalidationStrategy != 'manual' &&
      (timeDiff == null || timeDiff >= (storage.config.checkTime || 0))
    const dirtyCheck = storage.config.canDirty && storage.info.isDirty
    const staledCheck = storage.config.canStaled && storage.info.isStaled
    const initializationCheck = storage.state === DataHubLifecycle.INITIAL
    const lazyCheck =
      !storage.config.lazyLoad || storage.subscribers.size > 0 || storage.pipelines.some((p) => p.subscribers.size > 0)
    if (lazyCheck && (timeCheck || dirtyCheck || staledCheck || initializationCheck)) {
      await this.refreshStorage(storageID)
    }
  }

  private async refreshStorage(storageID: string): Promise<void> {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    storage.state = DataHubLifecycle.LOADING
    try {
      const rawData = await storage.dataGetter()
      let processedData: Array<any> = rawData
      // Procesar datos a través de las pipelines
      for (const pipeline of storage.pipelines) {
        for (const operation of pipeline.operations) {
          processedData = operation.execute(processedData)
        }
        const cleanData = [...processedData] // Crear una copia limpia de los datos procesados para evitar referencias compartidas
        pipeline.dataSignal.set(cleanData)
        this.notifyPipelineSubscribers(storageID, pipeline.name)
        pipeline.lastUpdate = new Date()
      }
      const cleanData = [...processedData] // Crear una copia limpia de los datos procesados para evitar referencias compartidas
      storage.dataSignal.set(cleanData)
      this.notifySubscribers(storageID)
      storage.info.lastUpdated = new Date()
      storage.info.isDirty = false
      storage.info.isStaled = false
      storage.state = DataHubLifecycle.LOADED
    } catch (error) {
      console.error(`Error refreshing storage ${storageID}:`, error)
      storage.state = DataHubLifecycle.ERROR
    }
  }

  private async refreshPipeline(storageID: string, pipelineName: string): Promise<void> {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    const pipeline = storage.pipelines.find((p) => p.name === pipelineName)
    if (!pipeline) {
      throw new Error(`Pipeline with name ${pipelineName} not found in storage ${storageID}`)
    }
    try {
      let processedData: Array<any> = storage.dataSignal.get()
      for (const operation of pipeline.operations) {
        processedData = operation.execute(processedData)
      }
      const cleanData = [...processedData] // Crear una copia limpia de los datos procesados para evitar referencias compartidas
      pipeline.dataSignal.set(cleanData)
      this.notifyPipelineSubscribers(storageID, pipelineName)
      pipeline.lastUpdate = new Date()
    } catch (error) {
      console.error(`Error refreshing pipeline ${pipelineName} in storage ${storageID}:`, error)
    }

    // Check if the storage have a next pipeline, if it does, refresh it too
    const pipelineIndex = storage.pipelines.findIndex((p) => p.name === pipelineName)
    if (pipelineIndex >= 0 && pipelineIndex < storage.pipelines.length - 1) {
      const nextPipeline = storage.pipelines[pipelineIndex + 1]
      await this.refreshPipeline(storageID, nextPipeline.name)
    }
  }

  public getStorageData<O>(storageID: string): O[] {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    return storage.dataSignal.get()
  }

  public markStorageDirty(storageID: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    storage.info.isDirty = true
  }

  public markStorageStaled(storageID: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    storage.info.isStaled = true
  }

  public addPipelineToStorage<I, O>(storageID: string, pipeline: DataHubPipeline<I, O>): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    storage.pipelines.push(pipeline)
  }

  public removePipelineFromStorage(storageID: string, pipelineName: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    storage.pipelines = storage.pipelines.filter((p) => p.name !== pipelineName)
  }

  public notifySubscribers(storageID: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    storage.subscribers.forEach((sub) => {
      sub.lastReceivedData = storage.dataSignal.get()
      sub.callback(storage.dataSignal.get())
    })
  }

  public notifySubscriber(storageID: string, subscriberID: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    const subscriber = Array.from(storage.subscribers).find((sub) => sub.id === subscriberID)
    if (!subscriber) {
      throw new Error(`Subscriber with ID ${subscriberID} not found in storage ${storageID}`)
    }
    subscriber.lastReceivedData = storage.dataSignal.get()
    subscriber.callback(storage.dataSignal.get())
  }

  public notifyPipelineSubscribers(storageID: string, pipelineName: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    const pipeline = storage.pipelines.find((p) => p.name === pipelineName)
    if (!pipeline) {
      throw new Error(`Pipeline with name ${pipelineName} not found in storage ${storageID}`)
    }
    pipeline.subscribers.forEach((sub) => {
      sub.lastReceivedData = pipeline.dataSignal.get()
      sub.callback(pipeline.dataSignal.get())
    })
  }

  public notifyPipelineSubscriber(storageID: string, pipelineName: string, subscriberID: string): void {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    const pipeline = storage.pipelines.find((p) => p.name === pipelineName)
    if (!pipeline) {
      throw new Error(`Pipeline with name ${pipelineName} not found in storage ${storageID}`)
    }
    const subscriber = Array.from(pipeline.subscribers).find((sub) => sub.id === subscriberID)
    if (!subscriber) {
      throw new Error(
        `Subscriber with ID ${subscriberID} not found in pipeline ${pipelineName} of storage ${storageID}`,
      )
    }
    subscriber.lastReceivedData = pipeline.dataSignal.get()
    subscriber.callback(pipeline.dataSignal.get())
  }

  public checkStorage(storageID: string): boolean {
    return this.storages.has(storageID)
  }

  public checkPipeline(storageID: string, pipelineName: string): boolean {
    const storage = this.storages.get(storageID)
    if (!storage) {
      throw new Error(`Storage with ID ${storageID} not found`)
    }
    return storage.pipelines.some((p) => p.name === pipelineName)
  }
}

const dataHubManager = new DataHubManager()
export default dataHubManager
